/**
 * Mobile workspace mode: flatten the tree to a list of panels, show the
 * focused one only, with a tab-row across the top to switch panels.
 *
 * Splits, maximize, and pop-out are hidden — they don't apply on phones.
 */
import { isPanelNode, type WorkspaceNode, type PanelNode } from '@/lib/workspace/types'
import { useWorkspaceStore } from '@/stores/workspaceStore'
import { VIEW_REGISTRY } from '@/lib/workspace/registry'
import { cn } from '@/lib/utils'
import { PanelContainer } from './PanelContainer'

function flatten(node: WorkspaceNode): PanelNode[] {
  if (isPanelNode(node)) return [node]
  return [...flatten(node.children[0]), ...flatten(node.children[1])]
}

export function MobilePanel({ node }: { node: WorkspaceNode }) {
  const focusedPanelId = useWorkspaceStore((s) => s.focusedPanelId)
  const focusPanel = useWorkspaceStore((s) => s.focusPanel)

  const panels = flatten(node)
  const active = panels.find((p) => p.id === focusedPanelId) ?? panels[0]
  if (!active) return null

  return (
    <div className="flex h-full flex-col">
      {panels.length > 1 && (
        <div className="flex border-b border-border bg-muted/20 overflow-x-auto scrollbar-thin">
          {panels.map((p, i) => {
            const def = p.views.find((v) => v.instanceId === p.activeViewInstanceId)
            const view = def ? VIEW_REGISTRY[def.viewId] : null
            const isActive = p.id === active.id
            const Icon = view?.icon
            return (
              <button
                key={p.id}
                onClick={() => focusPanel(p.id)}
                className={cn(
                  'flex items-center gap-1.5 px-3 py-2 text-xs font-mono whitespace-nowrap border-r border-border',
                  isActive
                    ? 'bg-background text-foreground border-b-2 border-b-primary'
                    : 'text-muted-foreground',
                )}
              >
                {Icon && <Icon className="h-3.5 w-3.5" />}
                <span>{view?.title ?? `Panel ${i + 1}`}</span>
              </button>
            )
          })}
        </div>
      )}
      <div className="flex-1 min-h-0">
        <PanelContainer panel={active} compact />
      </div>
    </div>
  )
}
